import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Container, Form, Spinner, Table } from 'react-bootstrap';
import { useCategoryMappings } from '../../../apps/hooks/oms/useCategoryMappings';
import { useProductCatTaxonomy } from '../../../apps/hooks/oms/useProductCatTaxonomy';
import type { CategoryMapping, CategoryMappingUpsert } from '../../../models/CategoryMapping';
import CategoryPicker from '../components/CategoryPicker';

const buildKey = (gpCategoryKr: string, gpSubCategoryKr: string): string =>
  JSON.stringify([gpCategoryKr.trim(), gpSubCategoryKr.trim()]);

interface MappingFields {
  gpCategoryKr: string;
  gpSubCategoryKr: string;
  wpCategory: string;
  leafCategorySlug: string;
  hsCode?: string;
}

const mergeUpsert = (base: MappingFields, patch: Partial<MappingFields>): CategoryMappingUpsert => {
  const trimmedHsCode = (patch.hsCode ?? base.hsCode ?? '').trim();
  return {
    gpCategoryKr: base.gpCategoryKr,
    gpSubCategoryKr: base.gpSubCategoryKr,
    leafCategorySlug: patch.leafCategorySlug ?? base.leafCategorySlug,
    wpCategory: patch.wpCategory ?? base.wpCategory,
    ...(trimmedHsCode ? { hsCode: trimmedHsCode } : {}),
  };
};

const emptyNewRow: CategoryMappingUpsert = {
  gpCategoryKr: '',
  gpSubCategoryKr: '',
  wpCategory: '',
  leafCategorySlug: '',
  hsCode: '',
};

const CategoryMappingPage = () => {
  const { mappings, isLoading, isError, saveMappings, isSaving } = useCategoryMappings();
  const taxonomy = useProductCatTaxonomy();

  const [search, setSearch] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [pendingByKey, setPendingByKey] = useState<Record<string, CategoryMappingUpsert>>({});
  const [newRow, setNewRow] = useState<CategoryMappingUpsert>(emptyNewRow);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState<number | null>(null);

  const effectiveRows = useMemo(() => {
    const rowsByKey = new Map<string, CategoryMapping>();
    mappings.forEach((mapping) => rowsByKey.set(buildKey(mapping.gpCategoryKr, mapping.gpSubCategoryKr), mapping));
    Object.entries(pendingByKey).forEach(([key, pending]) => {
      const existing = rowsByKey.get(key);
      rowsByKey.set(key, { ...existing, ...pending });
    });
    return [...rowsByKey.values()].sort(
      (first, second) =>
        first.gpCategoryKr.localeCompare(second.gpCategoryKr) ||
        first.gpSubCategoryKr.localeCompare(second.gpSubCategoryKr),
    );
  }, [mappings, pendingByKey]);

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return effectiveRows;
    }
    return effectiveRows.filter((row) =>
      [row.gpCategoryKr, row.gpSubCategoryKr, row.wpCategory, row.leafCategorySlug]
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [effectiveRows, search]);

  const pendingCount = Object.keys(pendingByKey).length;
  const taxonomyReady = !taxonomy.isLoading && !taxonomy.isError;

  const applyRowPatch = (row: CategoryMapping, patch: Partial<MappingFields>) => {
    const key = buildKey(row.gpCategoryKr, row.gpSubCategoryKr);
    setPendingByKey((previous) => {
      const current: MappingFields = { ...row, ...previous[key] };
      return { ...previous, [key]: mergeUpsert(current, patch) };
    });
  };

  const setRowSlug = (row: CategoryMapping, slug: string) => {
    applyRowPatch(row, { leafCategorySlug: slug, wpCategory: taxonomy.pathBySlug.get(slug) ?? '' });
  };

  const setRowHsCode = (row: CategoryMapping, hsCode: string) => {
    applyRowPatch(row, { hsCode });
  };

  const addNewRow = () => {
    const gpCategoryKr = newRow.gpCategoryKr.trim();
    const gpSubCategoryKr = newRow.gpSubCategoryKr.trim();
    if (!gpCategoryKr || !gpSubCategoryKr || !newRow.leafCategorySlug) {
      return;
    }
    const key = buildKey(gpCategoryKr, gpSubCategoryKr);
    setPendingByKey((previous) => ({
      ...previous,
      [key]: mergeUpsert({ ...newRow, gpCategoryKr, gpSubCategoryKr }, {}),
    }));
    setNewRow(emptyNewRow);
    setEditingKey(key);
  };

  const handleSave = async () => {
    setSaveError(null);
    setSavedCount(null);
    try {
      const response = await saveMappings(Object.values(pendingByKey));
      setPendingByKey({});
      setEditingKey(null);
      setSavedCount(response.totalSaved);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save category mappings');
    }
  };

  if (isLoading) {
    return (
      <Container fluid className="py-4 text-center text-muted">
        <Spinner animation="border" role="status" className="mb-2" />
        <div>Loading category mappings...</div>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">Failed to load category mappings. Check the internal API and try again.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div>
          <span className="h5 mb-1 d-block">GP to WP Category Mapping</span>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
            Maintain the Glowpick category pair to WooCommerce leaf slug mapping. Pick a real product category so the WP
            path is derived automatically. New WP categories must first be created in WP admin.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          {pendingCount > 0 && (
            <Badge bg="warning" text="dark">
              {pendingCount} unsaved
            </Badge>
          )}
          <Button variant="primary" onClick={handleSave} disabled={pendingCount === 0 || isSaving}>
            {isSaving ? 'Saving...' : `Save changes${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
          </Button>
        </div>
      </div>

      {savedCount !== null && (
        <Alert variant="success" dismissible onClose={() => setSavedCount(null)}>
          Saved {savedCount} category mapping{savedCount === 1 ? '' : 's'}.
        </Alert>
      )}
      {saveError && (
        <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}
      {taxonomy.isError && (
        <Alert variant="warning">
          Category taxonomy failed to load, so leaf-slug editing is disabled and existing slugs cannot be checked
          against WP. Reload the page to retry.
        </Alert>
      )}

      <div className="border rounded p-3 mb-3 bg-light">
        <div className="fw-semibold mb-2" style={{ fontSize: '0.9rem' }}>
          Add mapping
        </div>
        <div className="row g-2 align-items-start">
          <div className="col">
            <Form.Control
              size="sm"
              placeholder="GP category (KR)"
              value={newRow.gpCategoryKr}
              onChange={(event) => setNewRow({ ...newRow, gpCategoryKr: event.target.value })}
            />
          </div>
          <div className="col">
            <Form.Control
              size="sm"
              placeholder="GP subcategory (KR)"
              value={newRow.gpSubCategoryKr}
              onChange={(event) => setNewRow({ ...newRow, gpSubCategoryKr: event.target.value })}
            />
          </div>
          <div className="col">
            <CategoryPicker
              options={taxonomy.options}
              isLoading={taxonomy.isLoading}
              disabled={!taxonomyReady}
              size="sm"
              onSelect={(option) =>
                setNewRow({
                  ...newRow,
                  leafCategorySlug: option.slug,
                  wpCategory: taxonomy.pathBySlug.get(option.slug) ?? option.fullPath,
                })
              }
            />
            {newRow.leafCategorySlug && (
              <small className="text-muted d-block mt-1">
                <code>{newRow.leafCategorySlug}</code>
                {newRow.wpCategory ? ` — ${newRow.wpCategory}` : ' — not in taxonomy'}
              </small>
            )}
          </div>
          <div className="col">
            <Form.Control
              size="sm"
              placeholder="HS code (optional)"
              value={newRow.hsCode ?? ''}
              onChange={(event) => setNewRow({ ...newRow, hsCode: event.target.value })}
            />
          </div>
          <div className="col-auto">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={addNewRow}
              disabled={!newRow.gpCategoryKr.trim() || !newRow.gpSubCategoryKr.trim() || !newRow.leafCategorySlug}
            >
              Add
            </Button>
          </div>
        </div>
      </div>

      <Form.Control
        className="mb-2"
        placeholder="Search by GP category, subcategory, WP path, or slug..."
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
      <div className="text-muted mb-2" style={{ fontSize: '0.8rem' }}>
        {visibleRows.length} of {effectiveRows.length} mappings
      </div>

      <Table striped bordered hover size="sm" responsive>
        <thead>
          <tr>
            <th style={{ width: '14%' }}>GP Category</th>
            <th style={{ width: '16%' }}>GP Subcategory</th>
            <th style={{ width: '28%' }}>WP Category (derived)</th>
            <th style={{ width: '20%' }}>Leaf Slug</th>
            <th style={{ width: '12%' }}>HS Code</th>
            <th style={{ width: '10%' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => {
            const key = buildKey(row.gpCategoryKr, row.gpSubCategoryKr);
            const isPending = key in pendingByKey;
            const isEditing = editingKey === key;
            const slugInTaxonomy = row.leafCategorySlug ? taxonomy.hasSlug(row.leafCategorySlug) : false;
            return (
              <tr key={key} className={isPending ? 'table-warning' : undefined}>
                <td>{row.gpCategoryKr}</td>
                <td>{row.gpSubCategoryKr}</td>
                <td style={{ fontSize: '0.85rem' }}>{row.wpCategory || <span className="text-muted">(none)</span>}</td>
                <td>
                  {isEditing ? (
                    <>
                      <CategoryPicker
                        options={taxonomy.options}
                        isLoading={taxonomy.isLoading}
                        disabled={!taxonomyReady}
                        size="sm"
                        onSelect={(option) => setRowSlug(row, option.slug)}
                      />
                      <small className="text-muted d-block mt-1">
                        current: <code>{row.leafCategorySlug || '(empty)'}</code>
                      </small>
                    </>
                  ) : (
                    <span style={{ fontSize: '0.85rem' }}>
                      <code>{row.leafCategorySlug || '(empty)'}</code>
                      {taxonomyReady && row.leafCategorySlug && !slugInTaxonomy && (
                        <Badge bg="warning" text="dark" className="ms-1">
                          not in taxonomy
                        </Badge>
                      )}
                    </span>
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <Form.Control
                      size="sm"
                      value={row.hsCode ?? ''}
                      onChange={(event) => setRowHsCode(row, event.target.value)}
                    />
                  ) : (
                    row.hsCode || <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant={isEditing ? 'outline-secondary' : 'outline-primary'}
                    onClick={() => setEditingKey(isEditing ? null : key)}
                  >
                    {isEditing ? 'Done' : 'Edit'}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Container>
  );
};

export default CategoryMappingPage;
